import { useEffect, useState, useCallback, useMemo } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { Plus, User, ArrowUpRight, X, Sparkles, Download, Search, Archive, ArchiveRestore, AlertTriangle, Bell } from "lucide-react";
import { toast } from "sonner";
import ImportClientModal from "../components/ImportClientModal";
import CsvExportDialog from "../components/CsvExportDialog";
import { OnboardingTour } from "../components/onboarding/OnboardingTour";
import { GettingStartedCard } from "../components/onboarding/GettingStartedCard";

export default function Dashboard() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [form, setForm] = useState({ name: "", age: "", gender: "", sex: "unspecified", email: "", phone: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [archiveFilter, setArchiveFilter] = useState("active");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/clients");
      setClients(r.data);
    } catch (e) {
      toast.error("Could not load clients");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Mobile drawer "Export CSV" administrative action opens the same export dialog
  useEffect(() => {
    const openExport = () => setShowExport(true);
    window.addEventListener("pstr-open-csv-export", openExport);
    return () => window.removeEventListener("pstr-open-csv-export", openExport);
  }, []);

  // First-login welcome tour — auto-opens for brand-new accounts
  const [tourOpen, setTourOpen] = useState(false);
  useEffect(() => {
    if (loading) return;
    const done = localStorage.getItem("pstr_tour_done") === "1";
    const wants = localStorage.getItem("pstr_show_tour") === "1";
    if (!done && (wants || clients.length === 0)) setTourOpen(true);
  }, [loading, clients]);
  const closeTour = () => {
    localStorage.setItem("pstr_tour_done", "1");
    localStorage.removeItem("pstr_show_tour");
    setTourOpen(false);
  };
  const replayTour = () => {
    localStorage.removeItem("pstr_tour_done");
    setTourOpen(true);
  };

  // Unfinished sessions summary (drafts untouched for 24h+)
  const [unfinished, setUnfinished] = useState(null);
  useEffect(() => {
    api.get("/security/unfinished-sessions")
      .then((r) => {
        setUnfinished(r.data);
        if (r.data.count > 0 && r.data.oldest_days >= 7 && !sessionStorage.getItem("pstr_unfinished_alerted")) {
          sessionStorage.setItem("pstr_unfinished_alerted", "1");
          toast.error(`${r.data.count} unfinished session${r.data.count === 1 ? "" : "s"} — oldest is ${r.data.oldest_days} days old`, {
            description: "Review and complete or discard these drafts.",
          });
        }
      })
      .catch(() => {});
  }, []);

  // Same-day follow-up alerts — treatment done today, client-facing recs still outstanding.
  const [followups, setFollowups] = useState(null);
  const [showFollowupList, setShowFollowupList] = useState(false);
  const loadFollowups = useCallback(() => {
    const tz = new Date().getTimezoneOffset();
    api.get(`/push/followups/today?tz_offset=${tz}`)
      .then((r) => setFollowups(r.data))
      .catch(() => {});
  }, []);
  useEffect(() => {
    loadFollowups();
    const onFocus = () => loadFollowups();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadFollowups]);

  // Alphabetical by last name (fallback: first name), filtered by name/phone/email.
  // Searching spans ALL clients (archived stay searchable); otherwise the
  // Active/Archived/All filter applies.
  const archivedCount = useMemo(() => clients.filter((c) => c.archived).length, [clients]);
  const activeCount = useMemo(() => clients.filter((c) => !c.archived).length, [clients]);
  const allCount = clients.length;
  const visibleClients = useMemo(() => {
    const sortKey = (c) => {
      const parts = (c.name || "").trim().split(/\s+/);
      return (parts.length > 1 ? parts[parts.length - 1] : parts[0] || "").toLowerCase();
    };
    const sorted = [...clients].sort(
      (a, b) => sortKey(a).localeCompare(sortKey(b)) || (a.name || "").localeCompare(b.name || "")
    );
    const q = search.trim().toLowerCase();
    if (!q) {
      if (archiveFilter === "active") return sorted.filter((c) => !c.archived);
      if (archiveFilter === "archived") return sorted.filter((c) => c.archived);
      return sorted;
    }
    const qDigits = q.replace(/\D/g, "");
    return sorted.filter((c) => {
      if ((c.name || "").toLowerCase().includes(q)) return true;
      if ((c.email || "").toLowerCase().includes(q)) return true;
      const phoneDigits = (c.phone || "").replace(/\D/g, "");
      return qDigits.length > 0 && phoneDigits.includes(qDigits);
    });
  }, [clients, search, archiveFilter]);

  const restoreClient = async (e, clientId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.post(`/clients/${clientId}/restore`);
      toast.success("Client restored");
      load();
    } catch {
      toast.error("Could not restore client");
    }
  };

  const createClient = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/clients", {
        name: form.name,
        age: form.age ? parseInt(form.age) : null,
        gender: form.gender || null,
        sex: form.sex || "unspecified",
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        notes: form.notes,
      });
      setForm({ name: "", age: "", gender: "", sex: "unspecified", email: "", phone: "", notes: "" });
      setShowNew(false);
      toast.success("Client added");
      load();
    } catch {
      toast.error("Could not create client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="label-overline mb-2">Your studio</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-ink-900">Clients</h1>
        </div>
        <div className="flex flex-wrap gap-2 items-center justify-end">
          <button
            data-testid="batch-export-btn"
            onClick={() => setShowExport(true)}
            className="min-h-[48px] px-5 rounded-full bg-white border border-paper-300 hover:border-ink-900/40 text-ink-900 font-medium hidden md:flex items-center gap-2 transition-all active:scale-95 hover:shadow-sm"
          >
            <Download className="w-4 h-4" strokeWidth={2} />
            Export CSV
          </button>
          <button
            data-testid="import-client-btn"
            onClick={() => setShowImport(true)}
            className="min-h-[48px] px-5 rounded-full bg-white border border-paper-300 hover:border-ink-900/40 text-ink-900 font-medium flex items-center gap-2 transition-all active:scale-95 hover:shadow-sm"
          >
            <Sparkles className="w-4 h-4 text-sage-700" strokeWidth={2} />
            Import client
          </button>
          <button
            data-testid="add-client-btn"
            onClick={() => setShowNew(true)}
            className="min-h-[48px] px-6 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium flex items-center gap-2 transition-all active:scale-95 shadow-sm hover:shadow-md"
          >
            <Plus className="w-4 h-4" strokeWidth={2} />
            Add client
          </button>
        </div>
      </div>

      {!loading && (
        <GettingStartedCard clients={clients} onAddClient={() => setShowNew(true)} onReplayTour={replayTour} />
      )}

      {followups?.count > 0 && (
        <div className="mb-8" data-testid="today-followup-alert">
          <div className="rounded-3xl border border-sage-300 bg-sage-50 p-5">
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 bg-sage-500 text-white">
                <Bell className="w-5 h-5" strokeWidth={1.5} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-medium text-ink-900" data-testid="today-followup-count">
                  {followups.count} session{followups.count === 1 ? "" : "s"} need{followups.count === 1 ? "s" : ""} attention today
                </span>
                <span className="block text-sm text-ink-600 mt-0.5">
                  Client follow-up has not been completed.
                </span>
              </span>
              {followups.count === 1 ? (
                <Link
                  to={`/clients/${followups.items[0].client_id}/new-session?sid=${followups.items[0].session_id}`}
                  data-testid="today-followup-resume"
                  className="min-h-[40px] px-5 rounded-full bg-ink-900 text-white text-sm font-medium inline-flex items-center shrink-0 hover:bg-ink-900/90 transition-all"
                >
                  Resume
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowFollowupList((v) => !v)}
                  data-testid="today-followup-view"
                  className="min-h-[40px] px-5 rounded-full bg-ink-900 text-white text-sm font-medium inline-flex items-center shrink-0 hover:bg-ink-900/90 transition-all"
                >
                  {showFollowupList ? "Hide" : "View sessions"}
                </button>
              )}
            </div>
            {followups.count > 1 && showFollowupList && (
              <ul className="mt-4 pt-4 border-t border-sage-200 space-y-2" data-testid="today-followup-items">
                {followups.items.map((it) => (
                  <li key={it.session_id} className="flex items-center justify-between gap-3">
                    <span className="text-sm text-ink-800 truncate" data-testid={`today-followup-name-${it.session_id}`}>
                      {it.client_name}
                    </span>
                    <Link
                      to={`/clients/${it.client_id}/new-session?sid=${it.session_id}`}
                      data-testid={`today-followup-resume-${it.session_id}`}
                      className="min-h-[36px] px-4 rounded-full bg-white border border-sage-300 text-ink-900 text-sm font-medium inline-flex items-center shrink-0 hover:bg-sage-100 transition-all"
                    >
                      Resume
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {unfinished?.count > 0 && (
        <Link
          to={`/clients/${unfinished.items[0].client_id}`}
          data-testid="unfinished-sessions-card"
          className={`flex items-center gap-4 rounded-3xl border p-5 mb-8 transition-all hover:shadow-md ${
            unfinished.oldest_days >= 7
              ? "bg-red-50 border-red-200"
              : unfinished.oldest_days >= 3
                ? "bg-orange-50 border-orange-200"
                : "bg-amber-50 border-amber-200"
          }`}
        >
          <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
            unfinished.oldest_days >= 7 ? "bg-red-100 text-red-700" : unfinished.oldest_days >= 3 ? "bg-orange-100 text-orange-700" : "bg-amber-100 text-amber-700"
          }`}>
            <AlertTriangle className="w-5 h-5" strokeWidth={1.5} />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block font-medium text-ink-900">
              {unfinished.count} Unfinished Session{unfinished.count === 1 ? "" : "s"}
              {unfinished.oldest_days >= 3 && (
                <span className="ml-2 text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-full bg-orange-100 text-orange-800">
                  {unfinished.oldest_days >= 7 ? "Needs attention" : "Overdue"}
                </span>
              )}
            </span>
            <span className="block text-sm text-ink-500 mt-0.5">
              Oldest draft: {unfinished.oldest_days === 0 ? "today" : `${unfinished.oldest_days} day${unfinished.oldest_days === 1 ? "" : "s"} ago`} · {unfinished.items[0].client_name}
            </span>
          </span>
          <span className="min-h-[40px] px-5 rounded-full bg-ink-900 text-white text-sm font-medium inline-flex items-center shrink-0" data-testid="unfinished-review-now">
            Review Now
          </span>
        </Link>
      )}

      {(() => {
        if (loading) return <div className="text-ink-500">Loading…</div>;
        if (clients.length === 0) {
          return (
            <div data-testid="empty-state" className="bg-white border border-paper-200 rounded-3xl p-12 md:p-16 text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-paper-100 flex items-center justify-center">
                <User className="w-7 h-7 text-ink-500" strokeWidth={1.5} />
              </div>
              <div className="font-heading text-2xl font-light text-ink-900 mb-2">No clients yet</div>
              <p className="text-ink-700 max-w-md mx-auto mb-8">Add your first client to start capturing posture analyses and tracking sessions.</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <button
                  data-testid="empty-import-client-btn"
                  onClick={() => setShowImport(true)}
                  className="min-h-[48px] px-6 rounded-full bg-white border border-paper-300 hover:border-ink-900/40 text-ink-900 font-medium inline-flex items-center gap-2 transition-all"
                >
                  <Sparkles className="w-4 h-4 text-sage-700" /> Import from intake or history
                </button>
                <button
                  data-testid="empty-add-client-btn"
                  onClick={() => setShowNew(true)}
                  className="min-h-[48px] px-6 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium inline-flex items-center gap-2 transition-all"
                >
                  <Plus className="w-4 h-4" /> Add your first client
                </button>
              </div>
            </div>
          );
        }
        return (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="relative flex-1 min-w-[260px] max-w-xl">
                <Search className="w-4 h-4 text-ink-500 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={2} />
                <input
                  data-testid="client-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, phone, or email…"
                  className="w-full min-h-[48px] pl-11 pr-10 rounded-full bg-white border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none text-ink-900 placeholder:text-ink-500"
                />
                {search && (
                  <button
                    data-testid="client-search-clear"
                    onClick={() => setSearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-paper-100 flex items-center justify-center"
                  >
                    <X className="w-3.5 h-3.5 text-ink-500" strokeWidth={2} />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1 bg-paper-100 rounded-full p-1" data-testid="archive-filter-tabs">
                {[
                  { key: "active", label: `Active (${activeCount})` },
                  { key: "archived", label: `Archived (${archivedCount})` },
                  { key: "all", label: `All (${allCount})` },
                ].map((t) => (
                  <button
                    key={t.key}
                    data-testid={`archive-filter-${t.key}`}
                    onClick={() => setArchiveFilter(t.key)}
                    className={`min-h-[40px] px-4 rounded-full text-xs font-bold uppercase tracking-[0.1em] transition-colors ${
                      archiveFilter === t.key && !search ? "bg-ink-900 text-white" : "text-ink-500 hover:text-ink-900"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            {search.trim() && (
              <div className="text-xs text-ink-500 mb-4" data-testid="search-scope-note">
                Searching all clients, including archived.
              </div>
            )}
            {visibleClients.length === 0 ? (
              <div data-testid="no-search-results" className="bg-white border border-paper-200 rounded-3xl p-10 text-center text-ink-700">
                {search.trim() ? <>No clients match &ldquo;{search}&rdquo;.</> : "No clients in this view."}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {visibleClients.map((c) => (
              <Link
                key={c.id}
                to={`/clients/${c.id}`}
                data-testid={`client-card-${c.id}`}
                className="group bg-white border border-paper-200 rounded-2xl p-6 hover:border-sage-500/40 hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 rounded-xl bg-paper-100 flex items-center justify-center text-ink-700 font-heading text-lg font-medium">
                    {c.name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
                  </div>
                  <div className="flex items-center gap-2">
                    {c.unfinished_count > 0 && (
                      <span data-testid={`unfinished-badge-${c.id}`} className="text-[10px] font-bold uppercase tracking-[0.12em] px-2.5 py-1 rounded-full bg-orange-100 text-orange-800 inline-flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" strokeWidth={2} /> Unfinished
                      </span>
                    )}
                    {c.archived && (
                      <span data-testid={`archived-badge-${c.id}`} className="text-[10px] font-bold uppercase tracking-[0.12em] px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1">
                        <Archive className="w-3 h-3" strokeWidth={2} /> Archived
                      </span>
                    )}
                    <ArrowUpRight className="w-5 h-5 text-ink-500 opacity-0 group-hover:opacity-100 group-hover:text-sage-600 transition-all" strokeWidth={1.5} />
                  </div>
                </div>
                <div className="font-heading text-xl font-medium text-ink-900 mb-1">{c.name}</div>
                <div className="text-sm text-ink-500 mb-6">
                  {[c.age && `${c.age}y`, c.gender].filter(Boolean).join(" · ") || "—"}
                </div>
                <div className="flex items-center justify-between gap-2 pt-4 border-t border-paper-200">
                  <span className="label-overline">{c.session_count} {c.session_count === 1 ? "session" : "sessions"}</span>
                  {c.archived && (
                    <button
                      data-testid={`restore-client-btn-${c.id}`}
                      onClick={(e) => restoreClient(e, c.id)}
                      className="min-h-[36px] px-4 rounded-full bg-sage-500 hover:bg-sage-600 text-white text-xs font-medium inline-flex items-center gap-1.5 transition-all"
                    >
                      <ArchiveRestore className="w-3.5 h-3.5" strokeWidth={2} /> Restore
                    </button>
                  )}
                </div>
              </Link>
                ))}
              </div>
            )}
          </>
        );
      })()}

      {showNew && (
        <div className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg p-8 relative animate-fade-up">
            <button
              data-testid="close-new-client-btn"
              onClick={() => setShowNew(false)}
              className="absolute top-5 right-5 w-9 h-9 rounded-full hover:bg-paper-100 flex items-center justify-center"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <div className="label-overline mb-2">New client</div>
            <h2 className="font-heading text-2xl font-medium text-ink-900 mb-6">Add to your studio</h2>
            <form onSubmit={createClient} className="space-y-4">
              <div>
                <label className="label-overline block mb-2">Full name</label>
                <input
                  data-testid="new-client-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full min-h-[48px] px-4 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
                  placeholder="Sarah Chen"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-overline block mb-2">Age</label>
                  <input
                    data-testid="new-client-age"
                    type="number"
                    value={form.age}
                    onChange={(e) => setForm({ ...form, age: e.target.value })}
                    className="w-full min-h-[48px] px-4 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
                    placeholder="34"
                  />
                </div>
                <div>
                  <label className="label-overline block mb-2">Identity / pronouns <span className="text-ink-500 normal-case tracking-normal text-[11px]">— optional</span></label>
                  <input
                    data-testid="new-client-gender"
                    value={form.gender}
                    onChange={(e) => setForm({ ...form, gender: e.target.value })}
                    className="w-full min-h-[48px] px-4 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
                    placeholder="she/her, he/him, they/them…"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-overline block mb-2">Anatomical sex <span className="text-ink-500 normal-case tracking-normal text-[11px]">— body map reference</span></label>
                  <select
                    data-testid="new-client-sex"
                    value={form.sex}
                    onChange={(e) => setForm({ ...form, sex: e.target.value })}
                    className="w-full min-h-[48px] px-4 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
                  >
                    <option value="unspecified">Unspecified (neutral)</option>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-overline block mb-2">Email <span className="text-ink-500 normal-case tracking-normal text-[11px]">— matches Jotform</span></label>
                  <input
                    data-testid="new-client-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full min-h-[48px] px-4 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
                    placeholder="sarah@example.com"
                  />
                </div>
                <div>
                  <label className="label-overline block mb-2">Phone</label>
                  <input
                    data-testid="new-client-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full min-h-[48px] px-4 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
              <div>
                <label className="label-overline block mb-2">Intake notes</label>
                <textarea
                  data-testid="new-client-notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none resize-none"
                  placeholder="Chronic upper back tension, desk worker..."
                />
              </div>
              <button
                data-testid="save-client-btn"
                disabled={saving}
                className="w-full min-h-[52px] rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium transition-all disabled:opacity-50"
              >
                {saving ? "Saving…" : "Add client"}
              </button>
            </form>
          </div>
        </div>
      )}

      <ImportClientModal
        open={showImport}
        onClose={() => setShowImport(false)}
        onCreated={() => load()}
      />

      <OnboardingTour open={tourOpen} onClose={closeTour} />
      <CsvExportDialog
        open={showExport}
        onClose={() => setShowExport(false)}
        variant="batch"
      />
    </Layout>
  );
}
